/**
 * Le micro refusé par le navigateur, en séance : ce que l'élève en voit.
 *
 * Un refus ne se répare pas tout seul. Safari, une fois une demande du micro refusée dans une page, a
 * refusé d'office toutes les suivantes du même document — celles de chaque passage en Dialogue, et
 * celles des clics de l'élève, en moins d'une seconde, sans reposer la question. Sortir du live et y
 * revenir ne recharge pas la page : le refus tient. Sans un mot à l'écran, l'élève voit un micro barré
 * qui ne s'allume plus, croit qu'il marche, et son masteur ne l'entend pas (prod, 22/10).
 *
 * La règle : **en Dialogue, un micro refusé n'est jamais silencieux**. Une boîte le dit. Au téléphone,
 * « Réessayer » redemande d'abord dans le geste de l'élève (une demande sans geste n'a aucune chance
 * d'être reposée), puis, après un nouvel échec, ses Réglages. Au web, le chemin des réglages dès le
 * premier refus — ceux du navigateur, ou ceux de l'ordinateur quand c'est sa confidentialité qui bloque —
 * et « Recharger la page », qui efface le refus du document : redemander n'y sert à rien (Safari refuse
 * d'office toute demande du document, Chrome et Firefox gardent leur « Bloquer », Wajih 26/10).
 *
 *  - un refus vu par la salle (`room` : la connexion, un passage en Dialogue) ou par l'accès lu à
 *    l'ouverture (`permission`) pose le refus sans rouvrir une boîte que l'élève a fermée ;
 *  - un refus que l'élève provoque (`student` : son clic sur le micro, `retry` : « Réessayer ») la
 *    rouvre, et « Réessayer » refusé passe aux réglages (au téléphone) ;
 *  - le prochain passage en Dialogue la rouvre aussi : c'est le moment où le masteur veut l'entendre ;
 *  - le micro ouvert efface tout.
 *
 * En Concentré, rien ne s'affiche : le micro y est coupé par le masteur, la boîte parlerait d'un
 * micro dont l'élève n'a pas besoin.
 */
import { CAUSE, classifyMediaError } from "../../precall/deviceCheckRules"

/** D'où vient le refus. */
export type MicRefusalSource = "room" | "permission" | "student" | "retry"

/** Qui refuse : le navigateur (ou le téléphone), ou la confidentialité de l'ordinateur. */
export type MicBlocker = "browser" | "system"

export type MicRefusalState = {
    /** `null` : rien n'est refusé. Une fois l'ordinateur en cause, l'épisode le reste. */
    blocker: MicBlocker | null
    /** « Réessayer » a été refusé : la boîte passe aux réglages. */
    retried: boolean
    /** « Plus tard » : fermée jusqu'au prochain refus provoqué par l'élève ou au prochain Dialogue. */
    dismissed: boolean
}

export type MicRefusalEvent =
    | { type: "refused"; source: MicRefusalSource; blocker: MicBlocker }
    | { type: "opened" }
    | { type: "dismissed" }
    | { type: "dialogueEntered" }

export const initialMicRefusal: MicRefusalState = { blocker: null, retried: false, dismissed: false }

/** Ce que la boîte dit : d'abord « Réessayer » (au téléphone), puis le chemin des réglages. */
export type MicRefusalStep = "retry" | "settings"

export type MicRefusalView = {
    visible: boolean
    step: MicRefusalStep
    /** Refusé, boîte fermée ou non : le point du micro et « Un problème ? » le disent aussi. */
    refused: boolean
    blocker: MicBlocker
}

/** Le refus de l'épisode : une fois l'ordinateur en cause, il le reste (son geste de réparation est le sien). */
export function stickyBlocker(current: MicBlocker | null, next: MicBlocker): MicBlocker {
    return current === "system" ? current : next
}

export function micRefusalReducer(state: MicRefusalState, event: MicRefusalEvent): MicRefusalState {
    switch (event.type) {
        case "refused": {
            const byStudent = event.source === "student" || event.source === "retry"
            const next: MicRefusalState = {
                blocker: stickyBlocker(state.blocker, event.blocker),
                retried: state.retried || event.source === "retry",
                dismissed: byStudent ? false : state.dismissed,
            }
            const same =
                next.blocker === state.blocker && next.retried === state.retried && next.dismissed === state.dismissed
            return same ? state : next
        }
        case "opened":
            return state.blocker != null ? initialMicRefusal : state
        case "dismissed":
            return state.blocker != null && !state.dismissed ? { ...state, dismissed: true } : state
        case "dialogueEntered":
            return state.dismissed ? { ...state, dismissed: false } : state
    }
}

export function micRefusalView(
    state: MicRefusalState,
    live: { dialogue: boolean; micOn: boolean; web: boolean }
): MicRefusalView {
    const refused = state.blocker != null && !live.micOn
    return {
        visible: refused && !state.dismissed && live.dialogue,
        step: live.web || state.retried ? "settings" : "retry",
        refused,
        blocker: state.blocker ?? "browser",
    }
}

/**
 * Qui refuse le micro, d'après l'échec d'une demande — `null` pour un micro absent ou occupé. La règle
 * du test d'entrée (`classifyMediaError`) : c'est elle qui sépare le refus du navigateur de celui de la
 * confidentialité de l'ordinateur, dont le geste de réparation n'est pas le même.
 */
export function micBlockerOf(cause: unknown): MicBlocker | null {
    if (typeof cause !== "object" || cause == null) return null
    const classified = classifyMediaError(cause)
    if (classified === CAUSE.BLOCKED_BY_SYSTEM) return "system"
    return classified === CAUSE.DENIED ? "browser" : null
}
